import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { PermissionsService } from '../services/permissions.service';
import { AuthService } from '../services/auth.service';
import { firstValueFrom, catchError, of, timeout } from 'rxjs';

export const PermissionsGuard = async (route: ActivatedRouteSnapshot): Promise<boolean> => {
    const permissionsService = inject(PermissionsService);
    const authService = inject(AuthService);
    const router = inject(Router);

    const user = authService.getCurrentUser();
    if (!user) {
        router.navigate(['/login']);
        return false;
    }

    if (!permissionsService.isRindegastosUser()) {
        router.navigate(['/dashboard']);
        return false;
    }

    // Ensure permissions are loaded before checking
    if (!permissionsService.arePermissionsLoaded()) {
        const user = authService.getCurrentUser();
        const email = user?.username || (user as { email?: string })?.email || '';
        const isRindegastos = email.endsWith('@rindegastos.com');

        if (isRindegastos) {
            try {
                await firstValueFrom(
                    permissionsService.loadUserPermissionsByEmail().pipe(
                        timeout(5000),
                        catchError(err => {
                            console.error('Error loading permissions:', err);
                            return of([]);
                        })
                    )
                );
            } catch (error) {
                console.error('Timeout or error loading permissions:', error);
                router.navigate(['/dashboard']);
                return false;
            }
        } else if (user?.id) {
            try {
                await firstValueFrom(
                    permissionsService.loadUserPermissions().pipe(
                        timeout(5000),
                        catchError(err => {
                            console.error('Error loading permissions:', err);
                            return of([]);
                        })
                    )
                );
            } catch (error) {
                console.error('Timeout or error loading permissions:', error);
                router.navigate(['/dashboard']);
                return false;
            }
        }
    }

    const requiredModule = route.data['module'] as string;
    const requiredLevel = route.data['level'] as 'read' | 'write' | undefined;

    if (!requiredModule) {
        return true;
    }

    if (requiredLevel === 'write') {
        if (permissionsService.hasWritePermission(requiredModule)) {
            return true;
        }
    } else {
        if (permissionsService.hasReadPermission(requiredModule)) {
            return true;
        }
    }

    router.navigate(['/dashboard']);
    return false;
};

export const PermissionsGuardFactory = (module: string, level?: 'read' | 'write') => {
    return async (): Promise<boolean> => {
        const permissionsService = inject(PermissionsService);
        const authService = inject(AuthService);
        const router = inject(Router);

        const user = authService.getCurrentUser();
        if (!user) {
            router.navigate(['/login']);
            return false;
        }

        if (!permissionsService.isRindegastosUser()) {
            router.navigate(['/dashboard']);
            return false;
        }

        // Ensure permissions are loaded before checking
        if (!permissionsService.arePermissionsLoaded()) {
            const email = user?.username || (user as { email?: string })?.email || '';
            const isRindegastos = email.endsWith('@rindegastos.com');

            if (isRindegastos) {
                try {
                    await firstValueFrom(
                        permissionsService.loadUserPermissionsByEmail().pipe(
                            timeout(5000),
                            catchError(err => {
                                console.error('Error loading permissions:', err);
                                return of([]);
                            })
                        )
                    );
                } catch (error) {
                    console.error('Timeout or error loading permissions:', error);
                    router.navigate(['/dashboard']);
                    return false;
                }
            } else if (user?.id) {
                try {
                    await firstValueFrom(
                        permissionsService.loadUserPermissions().pipe(
                            timeout(5000),
                            catchError(err => {
                                console.error('Error loading permissions:', err);
                                return of([]);
                            })
                        )
                    );
                } catch (error) {
                    console.error('Timeout or error loading permissions:', error);
                    router.navigate(['/dashboard']);
                    return false;
                }
            }
        }

        if (level === 'write') {
            if (permissionsService.hasWritePermission(module)) {
                return true;
            }
        } else {
            if (permissionsService.hasReadPermission(module)) {
                return true;
            }
        }

        router.navigate(['/dashboard']);
        return false;
    };
};

/**
 * Blocks soporte, integraciones and infraestructura only while the temporary
 * zero-permission fallback is active. Users who already have permissions keep
 * the previous route behavior (these routes were not module-gated).
 */
export const temporaryExcludedModuleGuard = (module: string) => {
    return async (): Promise<boolean> => {
        const permissionsService = inject(PermissionsService);
        const authService = inject(AuthService);
        const router = inject(Router);

        if (!authService.getCurrentUser()) {
            return true;
        }

        if (!permissionsService.arePermissionsLoaded()) {
            try {
                await firstValueFrom(
                    permissionsService.ensureCurrentUserPermissionsLoaded().pipe(
                        timeout(5000),
                        catchError(err => {
                            console.error('Error loading permissions:', err);
                            return of([]);
                        })
                    )
                );
            } catch (error) {
                console.error('Timeout or error loading permissions:', error);
                return true;
            }
        }

        if (permissionsService.isBlockedByTemporaryDefault(module)) {
            router.navigate(['/dashboard']);
            return false;
        }

        return true;
    };
};
